import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {expenseinvoiceGetAll, expenseinvoiceExcel} from '../../../redux/slices/expenseinvoice.slice'
import {allCompanyLookup, projectLookup, basicLookup} from '../../../redux/slices/lookup.slice'
import {categoryGetAll} from '../../../redux/slices/category.slice'

import {ExpensePaymentStatus} from '../../../helpers'

function List() {
  const intl = useIntl()
  const dispatch = useDispatch()
  const pageKey = 'EXPENSES'

  const selectExpenseInvoiceData = (state) => ({
    expenseinvoice: state.expenseinvoice.expenseinvoice,
    loading: state.expenseinvoice.loading,
  })

  const selectLookupData = (state) => ({
    projectLookupList: state.lookup.projectLookupList,
    companyLookup: state.lookup.companyLookup,
    ExpenseType: state.lookup.ExpenseType,
    InvoiceKind: state.lookup.InvoiceKind,
    Bank: state.lookup.Bank,
  })

  const selectCategoryData = (state) => ({
    category: state.category.category,
  })

  const {expenseinvoice, loading} = useSelector(selectExpenseInvoiceData)
  const {projectLookupList, companyLookup, ExpenseType, InvoiceKind, Bank} =
    useSelector(selectLookupData)
  const {category} = useSelector(selectCategoryData)

  const [params, setParams] = useState()

  useEffect(() => {
    if (params) {
      dispatch(expenseinvoiceGetAll(params))
    }
  }, [dispatch, params])

  useEffect(() => {
    dispatch(allCompanyLookup())
    dispatch(projectLookup())
    dispatch(basicLookup('ExpenseType'))
    dispatch(basicLookup('InvoiceKind'))
    dispatch(basicLookup('Bank'))
    dispatch(categoryGetAll({SourceTypeId: 8}))
  }, [dispatch])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleFilterSubmit = useCallback((values) => {
    setParams({...values})
  }, [])

  const handleExcelExport = useCallback(() => {
    if (params) {
      dispatch(
        expenseinvoiceExcel({
          params,
          fileName: intl.formatMessage({id: 'BC.EXPENSES'}),
        })
      )
    }
  }, [dispatch, params, intl])

  const columns = useMemo(
    () => [
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'EXPENSEINVOICE.SEARCH.NAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'name',
        Cell: ({...props}) => (
          <CustomCellDouble
            item={props}
            to={`${props.data[props.row.index].expenseTypeName}/detail/${
              props.data[props.row.index].id
            }`}
            double='companyName'
          />
        ),
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'EXPENSEINVOICE.SEARCH.PROJECTNAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'projectName',
        Cell: ({...props}) => (
          <CustomCell
            item={props}
            to={`${props.data[props.row.index].expenseTypeName}/detail/${
              props.data[props.row.index].id
            }`}
          />
        ),
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'EXPENSEINVOICE.SEARCH.CATEGORYNAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'categoryName',
        Cell: ({...props}) => (
          <CustomCell
            item={props}
            to={`${props.data[props.row.index].expenseTypeName}/detail/${
              props.data[props.row.index].id
            }`}
          />
        ),
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'EXPENSEINVOICE.SEARCH.INVOICEKINDNAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'invoiceKindName',
        Cell: ({...props}) => (
          <CustomCell
            item={props}
            to={`${props.data[props.row.index].expenseTypeName}/detail/${
              props.data[props.row.index].id
            }`}
          />
        ),
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'EXPENSEINVOICE.SEARCH.EDITDATE'})}
            className='min-w-125px'
          />
        ),
        accessor: 'editDate',
        Cell: ({...props}) => (
          <CustomCell
            item={props}
            date='short'
            to={`${props.data[props.row.index].expenseTypeName}/detail/${
              props.data[props.row.index].id
            }`}
          />
        ),
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'EXPENSEINVOICE.SEARCH.DEFERMENTDATE'})}
            className='min-w-125px'
          />
        ),
        accessor: 'defermentDate',
        Cell: ({...props}) => (
          <CustomCellDouble
            item={props}
            date='short'
            to={`${props.data[props.row.index].expenseTypeName}/detail/${
              props.data[props.row.index].id
            }`}
            double='delayDay'
          />
        ),
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'EXPENSEINVOICE.SEARCH.TOTALPRICE'})}
            className='min-w-125px'
          />
        ),
        accessor: 'totalPrice',
        Cell: ({...props}) => (
          <CustomCell
            item={props}
            to={`${props.data[props.row.index].expenseTypeName}/detail/${
              props.data[props.row.index].id
            }`}
            price
          />
        ),
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'EXPENSEINVOICE.SEARCH.BALANCE'})}
            className='min-w-125px'
          />
        ),
        accessor: 'balance',
        Cell: ({...props}) => (
          <CustomCellDouble
            item={props}
            to={`${props.data[props.row.index].expenseTypeName}/detail/${
              props.data[props.row.index].id
            }`}
            double='paymentStatusName'
            price
          />
        ),
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'LIST.LASTMODIFIEDINFO'})}
            className='min-w-125px'
          />
        ),
        accessor: 'lastModifiedBy',
        Cell: ({...props}) => <CustomCellDouble item={props} double='lastModifiedDate' />,
      },
    ],
    [intl, handleSorting]
  )

  const buttons = useMemo(
    () => [
      {
        title: 'BUTTON.EXPENSE.SUNDRY.ADD',
        to: 'sundry/create',
        icon: '/images/icons/duotune/arrows/arr075.svg',
        variant: 'primary',
      },
      {
        title: 'BUTTON.EXPENSE.FAST.ADD',
        to: 'fast/create',
        icon: '/images/icons/duotune/arrows/arr075.svg',
        variant: 'primary',
      },
      {
        title: 'BUTTON.EXPENSE.DETAIL.ADD',
        to: 'detail/create',
        icon: '/images/icons/duotune/arrows/arr075.svg',
        variant: 'primary',
      },
      {
        title: 'BUTTON.OTHER',
        to: 'create',
        icon: '/images/icons/duotune/arrows/arr075.svg',
        variant: 'primary',
        list: [
          {
            title: 'BUTTON.EXPENSE.TAX.ADD',
            to: 'expenses/expenses/tax/create',
          },
          {
            title: 'BUTTON.EXPENSE.BANK.ADD',
            to: 'expenses/expenses/bank/create',
          },
        ],
      },
    ],
    []
  )

  const actionButtons = useMemo(
    () => [
      {
        title: 'BUTTON.EXCEL',
        onClick: handleExcelExport,
        icon: '/images/icons/duotune/arrows/arr044.svg',
        variant: 'light-primary',
      },
      {
        title: 'FILTER',
        icon: '/images/icons/duotune/general/gen031.svg',
        variant: 'light-primary',
        list: [
          {
            title: 'EXPENSEINVOICE.SEARCH.PROJECTNAME',
            data: projectLookupList,
            name: 'projectId',
          },
          {
            title: 'FILTER.EXPENSEINVOICE.COMPANYID',
            data: companyLookup,
            name: 'companyId',
          },
          {
            title: 'FILTER.EXPENSEINVOICE.EXPENSEPAYMENTSTATUSID',
            data: ExpensePaymentStatus,
            name: 'expensePaymentStatusId',
          },
          {
            title: 'FILTER.EXPENSEINVOICE.EXPENSETYPE',
            data: ExpenseType,
            name: 'expenseTypeId',
          },
          {
            title: 'EXPENSEINVOICE.SEARCH.EDITSTARTDATE',
            type: 'date',
            name: 'editStartDate',
          },
          {
            title: 'EXPENSEINVOICE.SEARCH.EDITENDDATE',
            type: 'date',
            name: 'editEndDate',
          },
          {
            title: 'EXPENSEINVOICE.SEARCH.CATEGORYNAME',
            data: category,
            name: 'categoryId',
          },
          {
            title: 'EXPENSEINVOICE.SEARCH.INVOICEKINDNAME',
            data: InvoiceKind,
            name: 'invoiceKindId',
          },
          {
            title: 'EXPENSEINVOICE.SEARCH.BANKNAME',
            data: Bank,
            name: 'bankId',
          },
        ],
      },
    ],
    [projectLookupList, companyLookup, ExpenseType, InvoiceKind, Bank, category, handleExcelExport]
  )

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.EXPENSES'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          buttons={buttons}
          actionButtons={actionButtons}
          onSubmit={handleFilterSubmit}
          pages={pageKey}
        />
        <KTCardBody>
          {!expenseinvoice && <ListLoading />}
          {expenseinvoice && (
            <Table
              items={expenseinvoice}
              cols={columns}
              handlePagination={handlePagination}
              paddless
              pages={pageKey}
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
